import { DESTINATIONS, type DestinationId } from "@/lib/destinations";

import { formatBadgeCount } from "./badgeCount";
import { Mark } from "./Mark";

interface RailProps {
  active: DestinationId;
  onNavigate: (id: DestinationId) => void;
  /** Cards due today — the Review badge. */
  dueCount?: number;
  /**
   * Jobs whose last run failed and that asked to be told (`notifyOnFailure`).
   *
   * On Setup, and in the SAME red as Review's. That is a change: the two badges used to be
   * different colours — blue for due, red for failed — and this theme has one accent with one
   * meaning, so both are things that want you and both are red. What tells them apart is which
   * destination carries the number, which is the only thing a reader was using anyway.
   */
  failedJobs?: number;
  /** Sync state, shown as the dot pinned to the bottom of the rail. */
  synced?: boolean;
}

/**
 * The desktop rail: **44px, tategaki, five destinations, a status dot pinned to the bottom.**
 *
 * Transcribed from `docs/designs/RailJP.dc.html`, which is the component the desktop and Stats
 * canvases both import rather than draw for themselves — twelve times and nine times. Every number
 * here was read back off that frame through `checks/_canvas.mjs`.
 *
 * **It is narrower than the 68px icon rail it replaces and the reading column gains the
 * difference**, which is the point rather than a side effect: the primary window is 1147px, so
 * 24px is 2% of the surface handed back to the thing the app is for.
 *
 * The destination is a full word set vertically (`writing-mode: vertical-rl` +
 * `text-orientation: upright`), so there is no glyph and no caption — **the label is the icon**.
 * The active item is a filled vermilion *nobori*, a banner the width of the cell.
 *
 * **A count sits inside the banner when active and on the red when not.** The badge's own fill is
 * the accent, so on an item already filled with it the badge would vanish; it takes `--void`
 * there instead. That is the one piece of state this rail draws two ways.
 *
 * Fixed, and present on every surface including Review, because Review is a destination rather
 * than a mode.
 */
export function Rail({ active, onNavigate, dueCount, failedJobs, synced = true }: RailProps) {
  return (
    <nav
      aria-label="移動先"
      data-check="nav"
      // Which of the two navigations this is. `data-check="nav"` is on both, deliberately —
      // a check that wants "the navigation" should not have to know which shape it is in — so
      // `check-mobile.mjs` needs this to assert that the rail is ABSENT rather than merely narrow.
      data-nav="rail"
      className="flex w-[var(--rail-w)] shrink-0 flex-col items-center gap-[3px] border-r border-line bg-rail px-0 py-[9px] box-border"
    >
      <Mark size={26} className="mb-[8px]" />

      {DESTINATIONS.map((d) => {
        const isActive = d.id === active;
        const count = d.id === "review" ? dueCount : d.id === "setup" ? failedJobs : undefined;
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => onNavigate(d.id)}
            data-check={`nav.${d.id}`}
            aria-current={isActive ? "page" : undefined}
            title={d.label}
            aria-label={d.label}
            className={[
              "flex w-[var(--rail-item-w)] flex-col items-center gap-[6px] pb-[8px] pt-[10px] box-border",
              isActive ? "rounded-[var(--r-2)] bg-accent-nobori" : "",
            ].join(" ")}
          >
            <span
              aria-hidden
              className={[
                // `upright` is what keeps 復習 reading top-to-bottom as two squared characters
                // rather than rotated onto their side.
                "[writing-mode:vertical-rl] [text-orientation:upright] font-jp text-[17px]/[1]",
                // Tighter tracking on the flag: the banner is 30px of red, and the looser inactive
                // spacing leaves the word floating in it.
                isActive
                  ? "font-bold tracking-[.1em] text-white"
                  : "font-medium tracking-[.16em] text-ink-3",
              ].join(" ")}
            >
              {d.word}
            </span>
            {count !== undefined && count > 0 && (
              <span
                /* **`badge.*`, deliberately NOT `nav.badge.*`.** Four checks count
                   `[data-check^="nav."]` inside the navigation and assert there are exactly five
                   items (`check-mobile.mjs`, `check-nav.mjs`, `check-review.mjs`,
                   `check-stats.mjs`). A badge is a descendant of one of those buttons, so a hook
                   under that prefix would make all four count six or seven — but only while a
                   count happened to be showing, which is the intermittent kind of red that gets
                   dismissed as flake. */
                data-check={`badge.${d.id}`}
                // Once the figure can be abbreviated it stops being recoverable from the badge,
                // and the button's own title is the destination word. Digits only, so this adds
                // nothing to `docs/CHROME.md`.
                title={count.toLocaleString()}
                className={[
                  /**
                   * **12px — the one deliberate exception to the app's 14px floor** (Alex, 15 Aug
                   * 2026, looking at `復習 998`). The floor is a rule about *reading*; this badge
                   * is always a figure, and Arabic numerals carry far more of their identity in
                   * the silhouette than kanji or lowercase Latin do. It is also the only place in
                   * the app with a hard width ceiling: `--rail-item-w` is 30px, so at 14px a
                   * three-digit count measured ~25px and filled the flag edge to edge.
                   *
                   * **The width, and the claim this comment used to make and got wrong** (日本-403).
                   * IBM Plex Mono 600 advances 0.6em, so at 12px a digit is 7.2px: three digits are
                   * 21.6px inside the 24px pill, and four are **28.8px, which spills out of the
                   * pill's own fill by 2.4px each side**. The old text called that fine because it
                   * stayed inside the 30px *flag* — but it is the red pill the digits visibly
                   * break out of, and that is what Alex reported. Four digits is ordinary here: the
                   * badge shows limit-capped `dueToday` for a deck of ~8,958 cards.
                   *
                   * So `formatBadgeCount` caps the string at three characters and `min-w` replaces
                   * the hard width: 1–3 digits still measure exactly 24px and are unchanged, while
                   * `1千+` measures 26.4px and sits inside the flag with 1.8px each side.
                   *
                   * **`min-w` works here only because this span is a flex ITEM.** `min-width` has
                   * no effect on an inline non-replaced element; the button above is `flex
                   * flex-col`, which blockifies its children. Move this out of that container and
                   * the 24px floor goes with it, silently — a one-digit badge collapses to 7.2px.
                   *
                   * `font-mono-jp` rather than `font-mono` because Plex Mono has no 千 and generic
                   * `monospace` always resolves, so without a JP family ahead of the generics the
                   * one glyph whose width this arithmetic depends on renders in an unknown face.
                   */
                  "h-[16px] min-w-[24px] rounded-[var(--r-2)] text-center font-mono-jp text-[12px]/[16px] font-semibold text-white",
                  isActive ? "bg-void" : "bg-accent-nobori",
                ].join(" ")}
              >
                {formatBadgeCount(count)}
              </span>
            )}
          </button>
        );
      })}

      <div className="flex-1" />
      <span
        title={synced ? "Synced with the hub" : "Not synced"}
        aria-label={synced ? "Synced" : "Not synced"}
        className={`mb-[8px] h-[7px] w-[7px] rounded-full ${synced ? "bg-ink-3" : "bg-line-strong"}`}
      />
    </nav>
  );
}
